import React, { useMemo, useState } from 'react';
import { Pressable, TouchableOpacity, View } from 'react-native';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import AppStyles from '@/constants/Styles';
import { useThemeColor } from '@/hooks/useThemeColor';
import { Engagement } from '@/types/engagement';

interface EngagementItemProps {
  engagement: Engagement;
  onPress?: (engagement: Engagement) => void;
  showProgressBars?: boolean;
  showStats?: boolean;
  showVoterCount?: boolean;
  style?: any;
}

function EngagementItem({ 
  engagement, 
  onPress,
  showProgressBars = true,
  showStats = true,
  showVoterCount = true,
  style
}: EngagementItemProps) {
  // Theme-aware colors from Colors.ts
  const borderColor = useThemeColor({}, 'border');
  const progressBackgroundColor = useThemeColor({}, 'progressBackground');
  const statsTextColor = useThemeColor({}, 'secondaryText');
  const cardBackgroundColor = useThemeColor({}, 'cardBackground');
  const progressVisitedColor = useThemeColor({}, 'progressVisited' as any);
  const progressNotInColor = useThemeColor({}, 'progressNotIn' as any);
  const progressContactedColor = useThemeColor({}, 'progressContacted' as any);
  const progressHighColor = useThemeColor({}, 'progressHigh' as any);
  const progressMediumColor = useThemeColor({}, 'progressMedium' as any);
  const progressLowColor = useThemeColor({}, 'progressLow' as any);

  // Memoize progress calculations to avoid recalculating on every render
  const progressData = useMemo(() => {
    const isGOTV = engagement.battle_plan_type === 'gotv-door-knocking' || engagement.battle_plan_type === 'gotv-phoning';

    if (isGOTV) {
      // For GOTV knocking, use response zone data
      const rzTotal = engagement.rz_total || 0;
      const rzWillNotVote = engagement.rz_will_not_vote || 0;
      const pledges = rzTotal - rzWillNotVote;
      const votersVoted = engagement.rz_voted || 0;
      const votersContacted = (engagement.rz_promised_to_vote || 0) + (engagement.rz_voters_not_in || 0);
      const votersNotYetContacted = pledges - votersContacted - votersVoted;
      // Treat NaN as 0
      const safeVotersNotYetContacted = isNaN(votersNotYetContacted) ? 0 : votersNotYetContacted;
      
      // Progress calculation based on completion (voted vs total)
      const votedProgress = pledges > 0 ? (votersVoted / pledges) * 100 : 0;
      const otherProgress = pledges > 0 ? 
        (votersContacted / pledges) * 100 : 0;
      
      // Color based on voters not yet voted: <5 green, 5-50 yellow, 50+ red, not started grey
      let progressColor;
      if (votersContacted + votersVoted === 0) {
        progressColor = '#808080'; // Grey for not started
      } else if (safeVotersNotYetContacted < 5) {
        progressColor = progressHighColor; // Green
      } else if (safeVotersNotYetContacted <= 50) {
        progressColor = progressMediumColor; // Yellow
      } else {
        progressColor = progressLowColor; // Red
      }
      
      return { 
        combinedProgress: votedProgress, 
        visitedProgress: votedProgress, 
        notInProgress: otherProgress, 
        progressColor,
        isGotvKnocking: true,
        pledges,
        votersNotYetContacted: safeVotersNotYetContacted
      };
    } else {
      // Standard canvassing logic
      // Denominator should be total addresses in the engagement.
      // `total_attempted_addresses` is useful to derive "contacted", but should not be the denominator
      // (otherwise visited + not_in + contacted always equals total_attempted and yields 100%).
      const totalAddresses = engagement.address_count || 0;
      const totalAttempted = engagement.total_attempted_addresses || 0;
      const addressesVisited = engagement.addresses_visited || 0;
      const addressesNotIn = engagement.addresses_not_in || 0;

      // Derive contacted as: attempted - visited - not_in.
      // Clamp so we never overflow the total address count in the segmented bar.
      const rawContacted = Math.max(0, totalAttempted - addressesVisited - addressesNotIn);
      const remainingAfterVisitedAndNotIn = Math.max(0, totalAddresses - addressesVisited - addressesNotIn);
      const addressesContacted = Math.min(rawContacted, remainingAfterVisitedAndNotIn);

      const attemptedAddresses = Math.min(
        totalAddresses,
        addressesVisited + addressesNotIn + addressesContacted
      );

      const combinedProgress = totalAddresses > 0 ? (attemptedAddresses / totalAddresses) * 100 : 0;
      const visitedProgress = totalAddresses > 0 ? (addressesVisited / totalAddresses) * 100 : 0;
      const notInProgress = totalAddresses > 0 ? (addressesNotIn / totalAddresses) * 100 : 0;
      const contactedProgress = totalAddresses > 0 ? (addressesContacted / totalAddresses) * 100 : 0;
      const progressColor = combinedProgress >= 80 ? progressHighColor : 
        combinedProgress >= 50 ? progressMediumColor : progressLowColor;
      
      return { 
        combinedProgress, 
        visitedProgress, 
        notInProgress, 
        contactedProgress,
        progressColor, 
        isGotvKnocking: false,
        totalAttempted,
        addressesVisited,
        addressesNotIn,
        addressesContacted
      };
    }
  }, [
    engagement.battle_plan_type,
    engagement.rz_total, 
    engagement.rz_will_not_vote, 
    engagement.rz_voted, 
    engagement.rz_voters_not_in, 
    engagement.rz_promised_to_vote,
    engagement.addresses_visited, 
    engagement.addresses_not_in, 
    engagement.total_attempted_addresses,
    engagement.address_count, 
    progressHighColor, 
    progressMediumColor, 
    progressLowColor
  ]);

  const { 
    combinedProgress, 
    visitedProgress, 
    notInProgress, 
    contactedProgress = 0,
    progressColor, 
    isGotvKnocking, 
    pledges, 
    votersNotYetContacted,
    addressesVisited = engagement.addresses_visited || 0,
    addressesNotIn = engagement.addresses_not_in || 0,
    addressesContacted = Math.max(0, (engagement.total_attempted_addresses || 0) - (engagement.addresses_visited || 0) - (engagement.addresses_not_in || 0))
  } = progressData;

  const [showCampaigners, setShowCampaigners] = useState(false);

  // Get active campaigner names
  const activeCampaigners = useMemo(() => {
    return (engagement.active_campaigner_names || []).filter((name): name is string => 
      name !== null && name !== undefined && name.trim() !== ''
    );
  }, [engagement.active_campaigner_names]);

  // Extract initials from a name
  const getInitials = (name: string): string => {
    if (!name || typeof name !== 'string') return '?';
    const trimmed = name.trim();
    if (trimmed.length === 0) return '?';
    
    const parts = trimmed.split(/\s+/).filter(p => p.length > 0);
    if (parts.length >= 2) {
      const first = parts[0];
      const last = parts[parts.length - 1];
      if (first && first.length > 0 && last && last.length > 0) {
        const firstChar = first.charAt(0);
        const lastChar = last.charAt(0);
        if (firstChar && lastChar) {
          return (firstChar + lastChar).toUpperCase();
        }
      }
    } else if (parts.length === 1) {
      const part = parts[0];
      if (part) {
        if (part.length >= 2) {
          return part.substring(0, 2).toUpperCase();
        } else if (part.length === 1) {
          return part.charAt(0).toUpperCase();
        }
      }
    }
    return '?';
  };

  // Get color for campaigner circle based on index
  const getCampaignerColor = (index: number): string => {
    const colors: string[] = [
      '#4CAF50', // Green
      '#2196F3', // Blue
      '#FF9800', // Orange
      '#9C27B0', // Purple
      '#F44336', // Red
      '#00BCD4', // Cyan
      '#FFC107', // Amber
      '#795548', // Brown
    ];
    return colors[index % colors.length] || '#9E9E9E';
  };

  const handlePress = () => {
    if (onPress) {
      onPress(engagement);
    }
    // No default navigation - parent components should provide onPress handler
  };

  const handleCampaignerToggle = (e: any) => {
    e.stopPropagation(); // Prevent triggering the main card press
    setShowCampaigners(!showCampaigners);
  };

  return (
    <ThemedView 
      style={[
        AppStyles.engagementItem, 
        { 
          marginBottom: 12, 
          borderWidth: 1, 
          borderColor, 
          backgroundColor: cardBackgroundColor 
        }, 
        style
      ]} 
    >
      <Pressable 
        style={{ flex: 1 }} 
        onPress={handlePress}
      >
      <View style={{ flex: 1 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <View style={{ flex: 1 }}>
            <ThemedText style={AppStyles.engagementName}>
              {`${engagement.number_within_battle_plan}${engagement.engagement_name ? `: ${engagement.engagement_name}` : ''}`}
            </ThemedText>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
            {!showStats && showVoterCount && (
              <ThemedText style={{ color: statsTextColor, fontSize: 12 }}>
                {engagement.voter_count} voters
              </ThemedText>
            )}
            {/* Campaigner avatars - positioned to the left of progress box */}
            {activeCampaigners.length > 0 && (
              <TouchableOpacity
                onPress={handleCampaignerToggle}
                style={{ 
                  flexDirection: 'row', 
                  alignItems: 'center', 
                  marginLeft: !showStats && showVoterCount ? 8 : 0,
                  marginRight: showProgressBars ? 8 : 0,
                  gap: 4
                }}
                activeOpacity={0.7}
              >
                {activeCampaigners.slice(0, 3).map((name, index) => (
                  <View
                    key={index}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 14,
                      backgroundColor: getCampaignerColor(index),
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderWidth: 1,
                      borderColor: cardBackgroundColor,
                      marginLeft: index > 0 ? -6 : 0, // Overlap circles slightly
                    }}
                  >
                    <ThemedText style={{ 
                      color: '#ffffff', 
                      fontSize: 10, 
                      fontWeight: 'bold' 
                    }}>
                      {getInitials(name)}
                    </ThemedText>
                  </View>
                ))}
                {activeCampaigners.length > 3 && (
                  <View
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 14,
                      backgroundColor: '#9E9E9E',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderWidth: 1,
                      borderColor: cardBackgroundColor,
                      marginLeft: -6,
                    }}
                  >
                    <ThemedText style={{ 
                      color: '#ffffff', 
                      fontSize: 9, 
                      fontWeight: 'bold' 
                    }}>
                      +{activeCampaigners.length - 3}
                    </ThemedText>
                  </View>
                )}
              </TouchableOpacity>
            )}
            {showProgressBars && (
              <View style={{ 
                backgroundColor: progressColor, 
                paddingHorizontal: 8, 
                paddingVertical: 4, 
                borderRadius: 12,
                minWidth: 50,
                alignItems: 'center'
              }}>
                <ThemedText style={{ color: '#ffffff', fontSize: 12, fontWeight: 'bold' }}>
                  {isGotvKnocking ? `${votersNotYetContacted} left` : `${Math.round(combinedProgress)}%`}
                </ThemedText>
              </View>
            )}
          </View>
        </View>
        
        {showProgressBars && (
          <View style={{ 
            height: 6, 
            backgroundColor: progressBackgroundColor, 
            borderRadius: 3, 
            marginBottom: 8,
            overflow: 'hidden',
            flexDirection: 'row'
          }}>
            {/* Visited segment (dark) */}
            {visitedProgress > 0 && (
              <View style={{ 
                height: '100%', 
                backgroundColor: progressVisitedColor, 
                width: `${visitedProgress}%`,
                borderRadius: 3
              }} />
            )}
            {/* Not In segment (medium) */}
            {notInProgress > 0 && (
              <View style={{ 
                height: '100%', 
                backgroundColor: progressNotInColor, 
                width: `${notInProgress}%`,
                borderRadius: 3
              }} />
            )}
            {/* Contacted segment (light) */}
            {contactedProgress > 0 && (
              <View style={{ 
                height: '100%', 
                backgroundColor: progressContactedColor, 
                width: `${contactedProgress}%`,
                borderRadius: 3
              }} />
            )}
          </View>
        )}
        
        {showStats && (
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            {isGotvKnocking ? (
              <>
                <ThemedText style={{ color: statsTextColor, fontSize: 14 }}>
                  {(() => {
                    const voted = engagement.rz_voted || 0;
                    return isNaN(voted) ? 0 : voted;
                  })()} of {pledges} RZ voters voted • {(() => {
                    const notInOrVoteLater = (engagement.rz_voters_not_in || 0) + (engagement.rz_promised_to_vote || 0);
                    return isNaN(notInOrVoteLater) ? 0 : notInOrVoteLater;
                  })()} not in / vote later
                </ThemedText>
              </>
            ) : (
              <>
                <ThemedText style={{ color: statsTextColor, fontSize: 14 }} numberOfLines={1}>
                  {(() => {
                    // Build text parts
                    const parts: string[] = [];
                    
                    // Always show visited count
                    parts.push(`${addressesVisited} of ${engagement.address_count} addresses visited`);
                    
                    // Show "not in" if it's greater than 0, otherwise hide it
                    if (addressesNotIn > 0) {
                      parts.push(`${addressesNotIn} not in`);
                    }
                    
                    // Show "contacted" only if "not in" is 0 (to save space)
                    // If "not in" exists, we hide "contacted" to save space
                    if (addressesNotIn === 0 && addressesContacted > 0) {
                      parts.push(`${addressesContacted} contacted`);
                    }
                    
                    return parts.join(' • ');
                  })()}
                </ThemedText>
                <ThemedText style={{ color: statsTextColor, fontSize: 12 }}>
                  {engagement.voter_count} voters
                </ThemedText>
              </>
            )}
          </View>
        )}

        {/* Expandable Campaigners Section */}
        {showCampaigners && activeCampaigners.length > 0 && (
          <View style={{ 
            marginTop: 8, 
            paddingTop: 8, 
            borderTopWidth: 1, 
            borderTopColor: borderColor 
          }}>
            <ThemedText style={{ 
              fontSize: 12, 
              fontWeight: '600', 
              marginBottom: 6,
              color: statsTextColor
            }}>
              Active Campaigners ({activeCampaigners.length})
            </ThemedText>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {activeCampaigners.map((name, index) => (
                <View
                  key={index}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: getCampaignerColor(index) + '20',
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    borderRadius: 12,
                    gap: 6,
                  }}
                >
                  <View
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      backgroundColor: getCampaignerColor(index),
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ThemedText style={{ 
                      color: '#ffffff', 
                      fontSize: 8, 
                      fontWeight: 'bold' 
                    }}>
                      {getInitials(name)}
                    </ThemedText>
                  </View>
                  <ThemedText style={{ 
                    fontSize: 12,
                    color: statsTextColor
                  }}>
                    {name}
                  </ThemedText>
                </View>
              ))}
            </View>
          </View>
        )}
      </View>
      </Pressable>
    </ThemedView>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default React.memo(EngagementItem);
